import {
  PostChannel,
  SchedulingVariantV1Channel,
} from '@/__generated__/graphql';
import { useSuspenseQuery } from '@apollo/client';
import { Input } from '@/components/shadcn/input';
import { Checkbox } from '@/components/shadcn/checkbox';
import SettingsTabTitle from '../settings-tab-title';
import { gql } from '@/__generated__';
import { useOrganizationContext } from '@/contexts/organization-context';
import { logError } from '@/lib/rum';
import { useState } from 'react';
import { cleanTypeName } from '@/lib/utils';

const schedulingVariantV1Query = gql(`
  query SocialMediaChannelsQuery(
    $input: SchedulingVariantV1GetByOrganizationInput!
  ) {
      schedulingVariantV1 {
      getByOrganization(input: $input) {
        ... on SchedulingVariantV1GetByOrganizationSuccess {
          items {
            item {
              id
              channels {
                channel
                name
              }
              frequency {
                weekly
              }
            }
          }
        }
      }
    }
  }
  `);

interface SocialMediaSectionProps {
  onChangeSchedulingVariant: (
    channels?: SchedulingVariantV1Channel[],
    frequency?: number,
    id?: string
  ) => void;
}

const SocialMediaChannels = ({
  onChangeSchedulingVariant,
}: SocialMediaSectionProps) => {
  const { organizationId } = useOrganizationContext();
  const { data: scheduleVariantV1Data, error } = useSuspenseQuery(
    schedulingVariantV1Query,
    {
      variables: {
        input: {
          organizationId: organizationId || '',
        },
      },
    }
  );
  const schedulingVariantV1 =
    scheduleVariantV1Data?.schedulingVariantV1?.getByOrganization?.items[0]
      ?.item;
  const [existingChannels, setExistingChannels] = useState(
    schedulingVariantV1?.channels
  );
  const [frequency, setFrequency] = useState(
    schedulingVariantV1?.frequency?.weekly
  );

  const onChannelChange = (name: PostChannel, checked: boolean) => {
    let newChannels = existingChannels ? [...(existingChannels || [])] : [];
    if (checked) {
      newChannels = newChannels.concat([{ channel: name }]);
    } else {
      newChannels = newChannels.filter((c) => c.channel !== name);
    }

    setExistingChannels(newChannels);
    onChangeSchedulingVariant(
      cleanTypeName(newChannels),
      undefined,
      schedulingVariantV1?.id
    );
  };

  if (error) {
    logError(
      'Something went wrong while loading organization grant settings',
      error
    );
  }

  return (
    <>
      <SettingsTabTitle title='Social Media Settings' />
      <p className='-mt-4'>Channels</p>
      <div className='mb-4 flex gap-6'>
        <div className='flex items-center gap-2'>
          Facebook
          <Checkbox
            variant={'maggie'}
            onCheckedChange={(isChecked: boolean) =>
              onChannelChange(PostChannel.Facebook, isChecked)
            }
            checked={
              !!existingChannels
                ?.map((c) => c.channel)
                ?.includes(PostChannel.Facebook)
            }
          />
        </div>
        <div className='flex items-center gap-2'>
          Instagram
          <Checkbox
            variant={'maggie'}
            onCheckedChange={(isChecked: boolean) =>
              onChannelChange(PostChannel.Instagram, isChecked)
            }
            checked={
              !!existingChannels
                ?.map((c) => c.channel)
                ?.includes(PostChannel.Instagram)
            }
          />
        </div>
        <div className='flex items-center gap-2'>
          Linkedin
          <Checkbox
            variant={'maggie'}
            onCheckedChange={(isChecked: boolean) =>
              onChannelChange(PostChannel.LinkedIn, isChecked)
            }
            checked={
              !!existingChannels
                ?.map((c) => c.channel)
                ?.includes(PostChannel.LinkedIn)
            }
          />
        </div>
      </div>
      <div>
        <p className='font-medium'>Frequency</p>
        <div className='flex items-center gap-4'>
          <Input
            type={'number'}
            className='max-w-32'
            value={frequency || ''}
            onChange={(e) => {
              setFrequency(Number(e.target.value));
              onChangeSchedulingVariant(
                undefined,
                Number(e.target.value),
                schedulingVariantV1?.id
              );
            }}
          />

          <div>Times a week</div>
        </div>
      </div>
    </>
  );
};

export default SocialMediaChannels;
